// src/pages/Marketplace/markets/CreateMarketModal.tsx
import React, { useState } from 'react';
import { ErrorBanner } from '@/pages/Admin/shared';
import type { MarketKind } from '@/pages/Marketplace/shared';
import { useMarketMutations } from '@/pages/Marketplace/shared';
import MarketMapPreview from './MarketMapPreview';

interface Props {
  apiUrl: string;
  accessToken: string | null;
  onClose: () => void;
  onCreated: (newId: string) => void;
}

type Step = 1 | 2 | 3;

interface Draft {
  kind: MarketKind | null;
  name: string;
  timezone: string;
  college_name: string;
  center_lat: string; center_lng: string; radius_km: string;
  city: string; state: string;
}

const EMPTY: Draft = {
  kind: null, name: '', timezone: 'America/Phoenix',
  college_name: '', center_lat: '', center_lng: '', radius_km: '',
  city: '', state: '',
};

function step2Valid(d: Draft): boolean {
  if (!d.name.trim() || !d.timezone) return false;
  if (d.kind === 'college') {
    const lat = parseFloat(d.center_lat), lng = parseFloat(d.center_lng), r = parseFloat(d.radius_km);
    return !!d.college_name.trim() && !Number.isNaN(lat) && !Number.isNaN(lng) && !Number.isNaN(r) && r > 0;
  }
  return !!d.city.trim() && !!d.state.trim();
}

export default function CreateMarketModal({ apiUrl, accessToken, onClose, onCreated }: Props): React.ReactElement {
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const { loading, error, create } = useMarketMutations(apiUrl, accessToken);

  const handleCreate = async () => {
    if (!draft.kind) return;
    const body = draft.kind === 'college'
      ? {
          kind: 'college' as const,
          name: draft.name,
          timezone: draft.timezone,
          college_name: draft.college_name,
          center_lat: parseFloat(draft.center_lat),
          center_lng: parseFloat(draft.center_lng),
          radius_km: parseFloat(draft.radius_km),
        }
      : {
          kind: 'city' as const,
          name: draft.name,
          timezone: draft.timezone,
          city: draft.city,
          state: draft.state,
        };
    const created = await create(body);
    if (created) onCreated(created.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-xl w-full max-w-xl">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">Create Market — Step {step} of 3</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>

        <div className="p-6 min-h-[280px]">
          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              {(['college', 'city'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setDraft((d) => ({ ...d, kind: k }))}
                  className={`p-6 rounded-xl border text-left transition-all ${
                    draft.kind === k
                      ? 'border-violet-600 bg-violet-950/30'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="text-2xl">{k === 'college' ? '🎓' : '🏙️'}</div>
                  <div className="text-sm text-white mt-2 capitalize">{k}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {k === 'college'
                      ? 'Radius-based market tied to a college campus.'
                      : 'City + state market for off-campus businesses.'}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && draft.kind && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-400">Market name</label>
                <input
                  className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-400">Timezone (IANA)</label>
                <select
                  value={draft.timezone}
                  onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                >
                  {Intl.supportedValuesOf('timeZone').map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              {draft.kind === 'college' ? (
                <>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400">College name</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.college_name}
                      onChange={(e) => setDraft((d) => ({ ...d, college_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Center lat</label>
                    <input
                      type="number" step="any"
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.center_lat}
                      onChange={(e) => setDraft((d) => ({ ...d, center_lat: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Center lng</label>
                    <input
                      type="number" step="any"
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.center_lng}
                      onChange={(e) => setDraft((d) => ({ ...d, center_lng: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400">Radius (km)</label>
                    <input
                      type="number" step="0.1" min="0"
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.radius_km}
                      onChange={(e) => setDraft((d) => ({ ...d, radius_km: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <MarketMapPreview
                      lat={parseFloat(draft.center_lat) || null}
                      lng={parseFloat(draft.center_lng) || null}
                      radiusKm={parseFloat(draft.radius_km) || null}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-gray-400">City</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.city}
                      onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">State</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                      value={draft.state}
                      onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value }))}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-300">
                Saving will create <strong>{draft.name}</strong> and auto-assign businesses whose
                city/location matches. Assignment runs in the background and usually takes a few seconds.
              </p>
              <pre className="bg-gray-950 border border-gray-800 rounded p-3 text-xs text-gray-300 overflow-x-auto">
{JSON.stringify(draft, null, 2)}
              </pre>
              {error && <ErrorBanner message={error} />}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-800 flex justify-between">
          <button
            onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}
            disabled={step === 1}
            className="px-3 py-2 text-sm text-gray-300 disabled:opacity-30 hover:text-white"
          >
            ← Back
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep((s) => ((s + 1) as Step))}
              disabled={(step === 1 && !draft.kind) || (step === 2 && !step2Valid(draft))}
              className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
            >
              {loading ? 'Creating…' : 'Create market'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
